"use client"

import React, { useEffect, useState } from "react"

interface ToggledCheckboxesProps {
  title: string
  checkboxes: string[]
  activeFilters: string[]
  setFilterFunction: React.Dispatch<React.SetStateAction<string[]>>
  initiallyOpen: boolean
}

const ToggledCheckboxes: React.FC<ToggledCheckboxesProps> = ({
  title,
  checkboxes,
  activeFilters,
  setFilterFunction,
  initiallyOpen,
}) => {
  const [open, setOpen] = useState<boolean>(false)
  const idFriendlyTitle = `${title.replace(/^[^a-z]+|[^\w:.-]+/gi, "")}`

  const toggleCheckboxes = () => setOpen((prevState) => !prevState)

  useEffect(() => {
    if (initiallyOpen) {
      setOpen(true)
    }
  }, [initiallyOpen])

  const handleInputChange = (e) => {
    const isChecked = e.currentTarget.checked
    const checkboxValue = e.currentTarget.value
    setFilterFunction((prevState) => {
      const newActiveFilters = [...prevState]
      if (isChecked) {
        newActiveFilters.push(checkboxValue)
      } else {
        newActiveFilters.splice(newActiveFilters.indexOf(checkboxValue), 1)
      }
      return newActiveFilters
    })
  }

  const sharedClasses =
    "body3 appearance-none text-blue-70 bg-gray-0 border border-solid border-2 border-blue-5 rounded-lg"
  const openFieldsetClasses =
    "p-6 mt-2 max-h-[1000px] opacity-100 [transition:opacity_.15s_ease_.1s,_max-height_0s_ease,_padding_0s_ease,_margin_0s_ease,_border_0s_ease]"
  const closedFieldsetClasses =
    "max-h-0 opacity-0 p-0 mt-0 border-0 [transition:opacity_.15s_ease,_max-height_0s_ease_.15s,_padding_0s_ease_.15s,_margin_0s_ease_.15s,_border_0s_ease_.15s]"
  const transitionClasses = open ? openFieldsetClasses : closedFieldsetClasses
  const checkboxFieldsetClasses = `${sharedClasses} w-full overflow-hidden ${transitionClasses}`
  const buttonClasses = `${sharedClasses} hover:border-blue-20 w-full pl-6 pr-17 py-4
    bg-[url('/icons/arrow-down-blue-70.svg')] bg-no-repeat bg-[calc(100%_-_16px)_center] bg-[length:22px_22px]`

  return (
    <div>
      <button
        className={buttonClasses}
        type="button"
        onClick={toggleCheckboxes}
      >
        {title}
      </button>
      <fieldset className={checkboxFieldsetClasses}>
        <legend className="sr-only">{title}</legend>
        {checkboxes.map((value, i) => (
          <div key={value} className="flex items-baseline">
            <input
              type="checkbox"
              id={`${idFriendlyTitle}${i}`}
              className="appearance-none uar-checkbox uar-checkbox-blue relative top-1"
              name={idFriendlyTitle}
              value={value}
              checked={activeFilters.includes(value)}
              onChange={handleInputChange}
            />
            <label
              htmlFor={`${idFriendlyTitle}${i}`}
              className="pl-6 xl:pl-[1.375rem]"
            >
              {value}
            </label>
          </div>
        ))}
      </fieldset>
    </div>
  )
}

export default ToggledCheckboxes
